// Whether the company's plan works on each phone (APP-26, D-091). The app reports what the phone allows Voxowa; the
// plan is the company's: the CRM needs the call log and notifications, and the work SIM on a phone with two; Coach and
// Autopilot also need the recordings folder. The after-call card and the battery setting help but are not required.

export type PermissionName =
  | "call_log"
  | "notifications"
  | "recordings"
  | "work_sim"
  | "after_call"
  | "battery"
  | "keeps_running";

export interface FeatureOnPhone {
  name: "CRM" | "Coach" | "Autopilot";
  /** Everything it needs is allowed on this phone. */
  works: boolean;
}

export interface PhoneReadiness {
  /** False until the phone runs an app that reports what it allows. */
  reported: boolean;
  features: FeatureOnPhone[];
  /** What the plan needs and the phone does not allow, in words for the owner. */
  problems: string[];
  /** What would help and is off. */
  advice: string[];
}

const NEEDED: Record<string, string> = {
  call_log: "Call log not allowed: this phone's calls do not reach Voxowa",
  notifications: "Notifications off: reminders do not reach this phone",
  work_sim: "No work SIM chosen on a phone with two SIMs",
  recordings: "No recordings folder: call review cannot hear this phone's calls",
};

const HELPFUL: Record<string, string> = {
  after_call: 'After-call card off: no "What next?" when a call ends',
  battery: "Battery saving on: syncing may pause when the screen is off",
  // The phone found calls it never heard of: its maker's battery manager stopped Voxowa (D-189).
  keeps_running: 'The phone stops Voxowa in the background: calls get no "What next?" and arrive late',
};

export function phoneReadiness(
  plan: { coach: boolean; autopilot: boolean } | undefined,
  permissions: unknown,
): PhoneReadiness {
  const allowed = permissions && typeof permissions === "object" && !Array.isArray(permissions)
    ? permissions as Partial<Record<PermissionName, unknown>>
    : null;
  const coach = plan?.coach ?? false;
  const autopilot = plan?.autopilot ?? false;
  if (!allowed) {
    return { reported: false, features: [], problems: [], advice: [] };
  }
  const off = (name: PermissionName) => allowed[name] === false;
  const crm = (["call_log", "notifications", "work_sim"] as PermissionName[]).filter(off);
  const reviews = coach || autopilot ? (["recordings"] as PermissionName[]).filter(off) : [];
  const features: FeatureOnPhone[] = [
    { name: "CRM", works: crm.length === 0 },
    ...(coach ? [{ name: "Coach" as const, works: crm.length === 0 && reviews.length === 0 }] : []),
    ...(autopilot ? [{ name: "Autopilot" as const, works: crm.length === 0 && reviews.length === 0 }] : []),
  ];
  return {
    reported: true,
    features,
    problems: [...crm, ...reviews].map((n) => NEEDED[n]),
    advice: (["keeps_running", "after_call", "battery"] as PermissionName[]).filter(off).map((n) => HELPFUL[n]),
  };
}

/** A company's add-ons as the database keeps them ({"coach": bool, "autopilot": bool}); anything else is off. */
export function addOnsOf(value: unknown): { coach: boolean; autopilot: boolean } {
  const v = value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  return { coach: v.coach === true, autopilot: v.autopilot === true };
}

/**
 * What the app asks for on each phone of a company with these add-ons (APP-26), in words for the platform: the
 * recordings folder with an add-on, or when the company keeps its recordings without AI (D-170).
 */
export function phoneAsksFor(addOns: { coach: boolean; autopilot: boolean }, keepsRecordings = false): string[] {
  return [
    "the call log",
    "notifications",
    "the work SIM (on a phone with two)",
    ...(addOns.coach || addOns.autopilot || keepsRecordings ? ["the recordings folder"] : []),
  ];
}
